import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
// `userService` through the barrel, the same way `@/hooks/useUsers` reaches it
// -- so a test that stubs `@/services` stubs this too, rather than half of the
// screen talking to a mock and half to the real axios client.
import { getErrorMessage, userService } from '@/services';
import { INSURER_ASSOCIATION_MISSING_HINT, type UserStatus, type UserWithStatus } from '@/services/users';

/** Labels for the three states, matching `User.STATUS_CHOICES` server-side. */
export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  active: 'Active',
  suspended: 'Suspended',
  blocked: 'Blocked',
};

/** What each state does to the account, said plainly. Used in confirmations. */
export const USER_STATUS_EFFECTS: Record<UserStatus, string> = {
  active: 'They will be able to log in again, and any failed-login lockout is cleared.',
  // "Signed out" is literal: the server blacklists every refresh token the
  // person holds, so each open session ends on its next refresh.
  suspended:
    'They will be signed out on every device and will not be able to log in. The login form will tell them the account is suspended.',
  blocked:
    'They will be signed out on every device and will not be able to log in. The login form will tell them the account is blocked.',
};

export const USER_STATUS_BADGE_VARIANT: Record<UserStatus, 'success' | 'warning' | 'error'> = {
  active: 'success',
  suspended: 'warning',
  blocked: 'error',
};

/**
 * Set a user to Active / Suspended / Blocked (board #180).
 *
 * The toast names the state that was actually stored — read back off the
 * server's response, not off what was requested. If the server ever answered
 * with something other than what was asked for, the admin sees the truth
 * rather than a confirmation of their own click.
 *
 * Kept out of `src/hooks/useUsers.ts` deliberately: that file's activate /
 * deactivate mutations are the two-state pair the screen has always had, and
 * they still work. This is the three-state writer they now delegate to.
 */
export function useSetUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: UserStatus }) =>
      userService.setStatus(id, status),
    onSuccess: (user: UserWithStatus, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
      const stored = user?.status;
      toast.success(
        stored
          ? `User set to ${USER_STATUS_LABELS[stored] ?? stored}.`
          : 'Status updated.'
      );
      if (stored === 'active' && user?.insurer_association_missing) {
        toast(INSURER_ASSOCIATION_MISSING_HINT, { duration: 10000 });
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
